import { useCallback, useEffect, useRef, useState } from "react";
import {
  runEvalUpload,
  startEvalUploadBacklogFlusher,
} from "../utils/evalUploadBacklog";
import {
  createEvalMediaRecorder,
  ensureEvalStreamHasAudio,
  EVAL_RECORD_TIMESLICE_MS,
  getEvalUserMedia,
  pickEvalVideoMimeType,
} from "../utils/evalVideoEncoding";
import {
  appendCaptureQos,
  resolveCaptureQosRunId,
  setCaptureQosContext,
  startCaptureQosSampling,
  stopCaptureQosSampling,
} from "../utils/captureQos";
import {
  getHeldEvalCameraStream,
  holdEvalCameraStream,
  resumeHeldEvalCameraStream,
} from "../utils/evalCameraPermission";
import { isIosReplayClient } from "../utils/tableReplayBuffer";
import { OFF_TABLE_CHRONO_SOURCE } from "../utils/stationCameraBinding";
import { phoneShouldRecordRemote } from "../utils/tableRecordingTarget";
import { tablePhoneShareFields } from "../utils/tablePhoneCoverage";
import { COACH_TABLE_VIDEO_SOURCE } from "./useCoachTableVideoRecording";
import { publishTablePhoneRecording } from "../utils/offTableRecordingFlag";

const COACH_VIDEO_SOURCE = "coach_video";

export function resolveRemoteCaptureSource(payload) {
  const source = String(payload?.source || "")
    .trim()
    .toLowerCase();
  if (source === OFF_TABLE_CHRONO_SOURCE) return OFF_TABLE_CHRONO_SOURCE;
  if (source === COACH_VIDEO_SOURCE) return COACH_VIDEO_SOURCE;
  // Table-cam Record button (coach tables dialog) — same session broadcast as
  // chrono, filtered by this phone's pinned table number.
  if (source === COACH_TABLE_VIDEO_SOURCE) return COACH_TABLE_VIDEO_SOURCE;
  return null;
}

/**
 * Keep a muted playsInline <video> decoding the camera so iOS Safari / CriOS
 * MediaRecorder actually receives frames (raw tracks alone often yield empty clips).
 */
function attachStreamToHiddenVideo(stream) {
  if (typeof document === "undefined" || !stream) return null;
  const video = document.createElement("video");
  video.setAttribute("playsinline", "true");
  video.setAttribute("webkit-playsinline", "true");
  video.playsInline = true;
  video.muted = true;
  video.autoplay = true;
  video.style.cssText =
    "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-99px;top:-99px;";
  video.srcObject = stream;
  document.body.appendChild(video);
  const playPromise = video.play?.();
  if (playPromise && typeof playPromise.catch === "function") {
    playPromise.catch(() => {
      /* autoplay may still require a prior Allow tap */
    });
  }
  return video;
}

function detachHiddenVideo(video) {
  if (!video) return;
  try {
    video.pause?.();
  } catch {
    /* ignore */
  }
  try {
    video.srcObject = null;
  } catch {
    /* ignore */
  }
  try {
    video.remove?.();
  } catch {
    /* ignore */
  }
}

function startMediaRecorderSafe(recorder) {
  const ios = isIosReplayClient();
  // Short timeslices restart the encoder often and freeze frames on iPhone.
  // Prefer a long window (or no timeslice) and flush with requestData on stop.
  const timeslice = ios ? EVAL_RECORD_TIMESLICE_MS : 1000;
  try {
    recorder.start(timeslice);
    return;
  } catch {
    /* fall through */
  }
  try {
    recorder.start();
  } catch (err) {
    throw err;
  }
}

function abortCapture(capture) {
  if (!capture) return;
  try {
    if (capture.recorder && capture.recorder.state !== "inactive") {
      capture.recorder.onstop = null;
      capture.recorder.stop();
    }
  } catch {
    /* ignore */
  }
  detachHiddenVideo(capture.previewVideo);
}

function captureBlobType(capture) {
  return (
    capture.mime ||
    capture.chunks[0]?.type ||
    (isIosReplayClient() ? "video/mp4" : "video/webm")
  );
}

function stopCaptureToBlob(capture) {
  const rec = capture.recorder;
  return new Promise((resolve, reject) => {
    rec.onstop = () => {
      resolve(new Blob(capture.chunks, { type: captureBlobType(capture) }));
    };
    try {
      // iOS flushes the final MP4 fragment only via requestData / stop.
      if (
        isIosReplayClient() &&
        typeof rec.requestData === "function" &&
        rec.state === "recording"
      ) {
        try {
          rec.requestData();
        } catch {
          /* ignore */
        }
      }
      if (rec.state !== "inactive") rec.stop();
      else resolve(new Blob(capture.chunks, { type: captureBlobType(capture) }));
    } catch (err) {
      reject(err);
    }
  });
}

/** One recording lane per covered table; chrono / player clips share one lane. */
function captureLane(payload) {
  const table = Number(payload?.table_number);
  return Number.isFinite(table) && table > 0 ? `table:${table}` : "remote";
}

/**
 * Player / table-phone listener: when the coach starts off-table chrono,
 * coach table-cam recording, or player-targeted coach video for this
 * user/device, record A/V and upload.
 * Turn camera recording on on the camera phone and off on the play phone so
 * only the second device of the same profile records.
 * The managing coach phone never runs this hook for recording.
 *
 * Each coach capture owns its recorder, chunks and metadata, so a clip can
 * keep uploading while the next one records. A wide-angle phone covering
 * several tables (`tableNumbers`) records each table in its own lane, so the
 * coach can start and stop tables independently from one camera.
 */
export function useOffTableChronoRecorder({
  socket,
  enabled = false,
  tsId = null,
  viewerUserId = null,
  tableNumber = null,
  tableNumbers = null,
  cameraSide = "A",
  videoQuality = "low",
}) {
  const [recording, setRecording] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [error, setError] = useState(null);

  /** Live captures by lane: { recorder, chunks, mime, meta, previewVideo }. */
  const activeRef = useRef(new Map());
  /** Captures whose camera is still opening, by lane: { captureId, stop: null|"upload"|"discard" }. */
  const startingRef = useRef(new Map());
  const tableNumberRef = useRef(tableNumber);
  const tableNumbersRef = useRef(tableNumbers);
  const cameraSideRef = useRef(cameraSide);
  const qualityRef = useRef(videoQuality);
  const enabledRef = useRef(enabled);
  const viewerUserIdRef = useRef(viewerUserId);

  useEffect(() => {
    tableNumberRef.current = tableNumber;
  }, [tableNumber]);
  useEffect(() => {
    tableNumbersRef.current = tableNumbers;
  }, [tableNumbers]);
  useEffect(() => {
    cameraSideRef.current = cameraSide;
  }, [cameraSide]);
  useEffect(() => {
    qualityRef.current = videoQuality;
  }, [videoQuality]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  useEffect(() => {
    viewerUserIdRef.current = viewerUserId;
  }, [viewerUserId]);

  const syncRecording = useCallback(() => {
    const active = activeRef.current.size > 0;
    publishTablePhoneRecording(active);
    setRecording(active);
  }, []);

  const discardRecorder = useCallback(() => {
    for (const starting of startingRef.current.values()) {
      starting.stop = "discard";
    }
    const captures = [...activeRef.current.values()];
    activeRef.current.clear();
    publishTablePhoneRecording(false);
    captures.forEach(abortCapture);
    setRecording(false);
  }, []);

  const matchesThisPhone = useCallback((payload) => {
    return phoneShouldRecordRemote(payload, {
      tableNumber: tableNumberRef.current,
      tableNumbers: tableNumbersRef.current,
      userId: viewerUserIdRef.current,
    });
  }, []);

  const finishCapture = useCallback(async (capture) => {
    const { meta } = capture;
    stopCaptureQosSampling();
    appendCaptureQos("recording_stop", {
      clip_id: meta.captureId || null,
      te_id: meta.teId,
    });
    setUploadCount((n) => n + 1);
    setError(null);
    try {
      const blob = await stopCaptureToBlob(capture);
      detachHiddenVideo(capture.previewVideo);

      if (!blob?.size) {
        setError("Recording produced an empty video — allow the camera and try again");
        return null;
      }

      const side = String(cameraSideRef.current || "A")
        .trim()
        .toUpperCase();
      const clipKind = meta.source || OFF_TABLE_CHRONO_SOURCE;
      const share = tablePhoneShareFields(
        meta.tableNumber,
        tableNumbersRef.current
      );
      const result = await runEvalUpload("eval_clip", {
        args: {
          mimeType: blob.type,
          teId: meta.teId,
          tableNumber: meta.tableNumber > 0 ? meta.tableNumber : undefined,
          cameraPosition: side === "B" ? "B" : "A",
          clipKind,
          recordedAt: meta.startedAt ? new Date(meta.startedAt) : new Date(),
          viewSetup: {
            clip_kind: clipKind,
            source: clipKind,
            capture_id: meta.captureId,
            table_number: meta.tableNumber,
            camera_position: side === "B" ? "B" : "A",
            receiver_user_id: meta.receiverUserId,
            ...(share || {}),
            table_number:
              meta.tableNumber > 0 ? meta.tableNumber : share?.table_number,
          },
        },
        blob,
      });
      if (result?.saved_on_phone) {
        console.info(
          "Coach recording saved on this phone. It uploads when the connection is back."
        );
      }
      return result;
    } catch (err) {
      detachHiddenVideo(capture.previewVideo);
      setError(err?.message || "Coach video upload failed");
      throw err;
    } finally {
      setUploadCount((n) => Math.max(0, n - 1));
    }
  }, []);

  const startLocalRecording = useCallback(
    async (payload) => {
      const teId = payload?.te_id != null ? String(payload.te_id) : null;
      if (!teId) return;
      const captureId =
        payload?.capture_id != null ? String(payload.capture_id) : null;
      const lane = captureLane(payload);

      const current = activeRef.current.get(lane);
      if (current) {
        if (!captureId || current.meta.captureId === captureId) return;
        // The stop for the previous capture on this table never reached
        // this phone — close that clip instead of ignoring every later Start.
        activeRef.current.delete(lane);
        syncRecording();
        finishCapture(current).catch((err) => {
          console.error("useOffTableChronoRecorder stale upload failed:", err);
        });
      }
      const pendingStart = startingRef.current.get(lane);
      if (pendingStart) {
        if (!captureId || pendingStart.captureId === captureId) return;
        pendingStart.stop = "upload";
      }

      const starting = { captureId, stop: null };
      startingRef.current.set(lane, starting);
      setError(null);
      let previewVideo = null;
      try {
        // Prefer the stream warmed by the Allow-camera tap — required on iOS /
        // Android Chrome because a socket event is not a user gesture.
        let stream = resumeHeldEvalCameraStream() || getHeldEvalCameraStream();
        if (stream) {
          await ensureEvalStreamHasAudio(stream);
        } else {
          stream = await getEvalUserMedia({
            facingMode: "environment",
            audio: true,
          });
          // Keep in the session hold so the next coach Record does not re-prompt.
          holdEvalCameraStream(stream);
        }
        if (starting.stop === "discard") return;

        previewVideo = attachStreamToHiddenVideo(stream);
        // Give Safari a beat to decode the first frame before MediaRecorder starts.
        if (isIosReplayClient()) {
          await new Promise((resolve) => window.setTimeout(resolve, 120));
        }
        if (starting.stop === "discard") {
          detachHiddenVideo(previewVideo);
          return;
        }

        const mime = pickEvalVideoMimeType();
        const recorder = createEvalMediaRecorder(
          stream,
          qualityRef.current || "low",
          mime
        );
        const chunks = [];
        recorder.ondataavailable = (ev) => {
          if (ev.data && ev.data.size > 0) chunks.push(ev.data);
        };
        const meta = {
          teId,
          captureId,
          tableNumber:
            Number(payload?.table_number) || Number(tableNumberRef.current) || 0,
          startedAt: payload?.started_at || new Date().toISOString(),
          receiverUserId:
            payload?.receiver_user_id != null
              ? String(payload.receiver_user_id)
              : null,
          source: resolveRemoteCaptureSource(payload) || OFF_TABLE_CHRONO_SOURCE,
        };
        const capture = { recorder, chunks, mime, meta, previewVideo };
        startMediaRecorderSafe(recorder);

        if (starting.stop === "discard") {
          abortCapture(capture);
          return;
        }
        setCaptureQosContext({
          flow:
            meta.source === COACH_TABLE_VIDEO_SOURCE
              ? "coach_table"
              : "off_table_chrono",
          session_id: teId,
          clip_id: captureId,
          run_id: resolveCaptureQosRunId(),
        });
        appendCaptureQos("recording_start", {
          path: "mediarecorder",
          source: meta.source,
        });
        startCaptureQosSampling();

        if (starting.stop === "upload") {
          // Coach stopped while the camera was still opening.
          await finishCapture(capture);
          return;
        }
        activeRef.current.set(lane, capture);
        publishTablePhoneRecording(true);
        setRecording(true);
      } catch (err) {
        detachHiddenVideo(previewVideo);
        throw err;
      } finally {
        if (startingRef.current.get(lane) === starting) {
          startingRef.current.delete(lane);
        }
      }
    },
    [finishCapture, syncRecording]
  );

  useEffect(() => startEvalUploadBacklogFlusher((job) => job()), []);

  useEffect(() => {
    if (!socket || !enabled) return undefined;

    const onStart = (payload = {}) => {
      if (!enabledRef.current) return;
      if (!resolveRemoteCaptureSource(payload)) return;
      if (!matchesThisPhone(payload)) return;
      startLocalRecording(payload).catch((err) => {
        console.error("useOffTableChronoRecorder start failed:", err);
        setError(err?.message || "Failed to start coach recording");
      });
    };

    const onStop = (payload = {}) => {
      const captureId =
        payload?.capture_id != null ? String(payload.capture_id) : "";
      const sameCapture = (id) => Boolean(captureId && id && id === captureId);
      const mine = matchesThisPhone(payload);
      const discard = payload?.discard === true;
      const lane = captureLane(payload);
      const byLane = mine && resolveRemoteCaptureSource(payload);

      for (const [startLane, starting] of startingRef.current) {
        if (
          sameCapture(starting.captureId) ||
          (!captureId && byLane && startLane === lane)
        ) {
          starting.stop = discard ? "discard" : "upload";
        }
      }

      let hit = null;
      for (const [captureLaneKey, capture] of activeRef.current) {
        const ownId = capture.meta.captureId;
        const matches =
          captureId && ownId
            ? captureId === ownId
            : Boolean(byLane) && captureLaneKey === lane;
        if (matches) {
          hit = [captureLaneKey, capture];
          break;
        }
      }
      if (!hit) return;
      const [hitLane, capture] = hit;
      activeRef.current.delete(hitLane);
      syncRecording();
      if (discard) {
        stopCaptureQosSampling();
        abortCapture(capture);
        return;
      }
      finishCapture(capture).catch((err) => {
        console.error("useOffTableChronoRecorder stop/upload failed:", err);
      });
    };

    socket.on("tableVideoRecordingStart", onStart);
    socket.on("tableVideoRecordingStop", onStop);
    return () => {
      socket.off("tableVideoRecordingStart", onStart);
      socket.off("tableVideoRecordingStop", onStop);
    };
  }, [
    socket,
    enabled,
    matchesThisPhone,
    startLocalRecording,
    finishCapture,
    syncRecording,
  ]);

  /* Tell the coach chrono panel this phone can record. */
  useEffect(() => {
    if (!socket || !enabled || !tsId) return undefined;
    const announce = (online = true) => {
      socket.emit("announceChronoCamera", {
        ts_id: String(tsId),
        user_id: viewerUserId != null ? String(viewerUserId) : null,
        table_number:
          Number(tableNumber) > 0 ? Number(tableNumber) : null,
        online,
      });
    };
    const onConnect = () => announce(true);
    announce(true);
    socket.on("connect", onConnect);
    return () => {
      socket.off("connect", onConnect);
      announce(false);
    };
  }, [socket, enabled, tsId, viewerUserId, tableNumber]);

  useEffect(() => {
    if (!enabled) discardRecorder();
  }, [enabled, discardRecorder]);

  useEffect(() => () => discardRecorder(), [discardRecorder]);

  return {
    recording,
    uploading: uploadCount > 0,
    error,
  };
}

export default useOffTableChronoRecorder;
